/**
 * Legacy WordPress uploads. Known PDFs are preserved at their new home under
 * /downloads/; old images go to the page they illustrated (legacyUploadTarget
 * below); everything else redirects to the homepage.
 */
export const LEGACY_UPLOAD_REDIRECTS: Record<string, string> = {
  "uploads/2023/10/RESTORE_download_Denver_SoftCover_sept_2023_online_v12_1.pdf":
    "/downloads/RESTORE-Hair-Brochure-Colorado.pdf",
  "uploads/2023/10/RESTORE_download_Nationwide_SoftCover_sept_2023_online_v12_1_compressed-1.pdf":
    "/downloads/RESTORE-Hair-Brochure.pdf",
  // The 2023 digital booklet went to the home page. RESTORE's search cleanup
  // log (16 Oct 2026) asks for the current brochure, like the PDF above.
  "uploads/2023/09/RESTORE_Hair_Digital_Booklet.pdf": "/downloads/RESTORE-Hair-Brochure.pdf",
  "uploads/2025/08/restore_presentation_mobile_v26-1.pdf":
    "/downloads/restore_presentation_mobile_v26-1.pdf",
  // Patient instructions RESTORE still sends by text and email (Brian, 28 Oct
  // 2026). Pre-op is the newest copy in the WordPress media library, "Denver
  // Pre-Op Instructions V2.4" (Feb 2024); WordPress already sent V2.0 and
  // V2.1 on to the newest. The medical treatments PDF is byte-identical to
  // the archived 2022/07 original. The post-op PDF is gone from WordPress
  // and the archive, so it goes to the post-op instructions page until
  // RESTORE sends the file.
  "uploads/2022/08/Denver-Pre-Op-Instructions-V2.0.pdf":
    "/downloads/Denver-Pre-Op-Instructions.pdf",
  "uploads/2022/10/Denver-Pre-Op-Instructions-V2.1.pdf":
    "/downloads/Denver-Pre-Op-Instructions.pdf",
  "uploads/2023/08/Denver-Pre-Op-Instructions-3.pdf": "/downloads/Denver-Pre-Op-Instructions.pdf",
  "uploads/2022/08/Denver-Post-Operative-FUE-Instructions-V2.0.pdf": "/denver-post-op/",
  "uploads/2022/09/Denver-Post-Operative-FUE-Instructions-V2.0-1.pdf": "/denver-post-op/",
  "uploads/2022/07/MEDICAL_TREATMENTS-1.5.2021_HSC_Colorado-1.pdf":
    "/downloads/Medical-Treatments-Colorado.pdf",
  "uploads/2026/04/MEDICAL_TREATMENTS-1.5.2021_HSC_Colorado-1-1.pdf":
    "/downloads/Medical-Treatments-Colorado.pdf",
  // Images still used inside migrated blog posts, recovered from the
  // WordPress staging copy on 30 Sep 2026.
  "uploads/2022/04/FUE-NO-SCAR-STRIP-e1651161519818.jpg": "/images/legacy/fue-no-scar-strip.jpg",
  "uploads/2022/04/IMG_7607-1-scaled-e1651161651179.jpg": "/images/legacy/img-7607.jpg",
  "uploads/2022/05/img1-1.jpg": "/images/legacy/img1-1.jpg",
  // Newer brochure and consultation PDFs from the WordPress media library
  // (old-URL audit, 1 Nov 2026). They went to the home page. On WordPress,
  // /consultation/ sent visitors to v32-1 and /brochure/ to the Aug 2025 v6
  // brochure. Each goes to the copy this site serves for the same document.
  "uploads/2026/03/restore_presentation_mobile_v32-1.pdf":
    "/downloads/restore_presentation_mobile_v26-1.pdf",
  "uploads/2025/09/restore_presentation_mobile_v31.pdf":
    "/downloads/restore_presentation_mobile_v26-1.pdf",
  "uploads/2025/06/restore_presentation_mobile_June25.pdf":
    "/downloads/restore_presentation_mobile_v26-1.pdf",
  "uploads/2025/04/restore_presentation_mobile_v24-1.pdf":
    "/downloads/restore_presentation_mobile_v26-1.pdf",
  "uploads/2024/03/restore_presentation_mobile_v22.pdf":
    "/downloads/restore_presentation_mobile_v26-1.pdf",
  "uploads/2025/10/RESTORE_download_Nationwide_SoftCover_aug_2025_v6.pdf":
    "/downloads/RESTORE-Hair-Brochure.pdf",
  "uploads/2025/10/RESTORE_download_Nationwide_SoftCover_aug_2025_online_v5.pdf":
    "/downloads/RESTORE-Hair-Brochure.pdf",
};

// Old images that other sites link to (Ahrefs: 49 image URLs, up to 75
// referring domains each) or that Search Console showed, mostly celebrity
// before-and-after photos. The files are gone from WordPress too. Sending
// them to the home page reads as a soft 404; the matching results or topic
// page keeps the link's meaning. First match wins. Files that match nothing
// still go to the home page.
const IMAGE = /\.(?:jpe?g|png|gif|webp)$/i;
const IMAGE_TOPICS: [RegExp, string][] = [
  [/urlacher|brian|(?:^|[-_])bu[-_]|_vbu/, "/results/brian-urlacher/"],
  [/deion|(?:^|[-_])ds[-_]/, "/results/deion-sanders/"],
  [/ryne|(?:^|[-_])rs[-_]/, "/results/ryne-sandberg/"],
  [/forte|(?:^|[-_])matt[-_]/, "/results/matt-forte/"],
  [/heath/, "/results/heath-evans/"],
  [/zach/, "/results/zach-johnson/"],
  [/mike[-_]b|bibby/, "/results/mike-bibby/"],
  [/bickell/, "/results/bryan-bickell/"],
  [/kanell/, "/results/danny-kanell/"],
  [/cornelison/, "/results/jim-cornelison/"],
  [/kevin[-_]s|streelman/, "/results/kevin-streelman/"],
  [/prp/, "/prp-treatment-for-hair-restoration-its-effectiveness-and-all-things-to-know-about-prp-therapy/"],
  [/norwood/, "/what-is-the-norwood-scale/"],
  [/genetic|baldness-gene/, "/baldness-gene-come-mother-father/"],
  [/harris|dr\.?james/, "/doctors/dr-james-harris/"],
  [/no-shave/, "/no-shave-fue-hair-transplant/"],
  [/pricing/, "/pricing/"],
  [/before|after|result|progress|growth/, "/results/"],
];

/** The 301 target for a path under /wp-content/ (without that prefix). */
export function legacyUploadTarget(legacyPath: string): string {
  const known = LEGACY_UPLOAD_REDIRECTS[legacyPath];
  if (known) return known;
  if (IMAGE.test(legacyPath)) {
    const file = (legacyPath.split("/").pop() ?? "").toLowerCase();
    for (const [pattern, target] of IMAGE_TOPICS) if (pattern.test(file)) return target;
  }
  return "/";
}
